import { Fragment, PropsWithChildren, useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';

import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Tab from '@mui/material/Tab';
import MUITabs from '@mui/material/Tabs';

import { ReactComponent as AngleDown } from '~/assets/images/angle-down.svg';
import useResponsive from '~/hooks/useResponsive';

import Drawer from './Drawer';

type TabPage = {
  title: string;
  path: string;
  disabled?: boolean;
  dataTestId?: string;
};

export type TabsProps = {
  pages: Array<TabPage>;
};

const baseTabSx = {
  justifyContent: 'flex-start',
  textTransform: 'none',
  fontWeight: 500,
  '&.Mui-selected': {
    color: 'text.primary',
  },
};

const mobileTabSx = {
  ...baseTabSx,
  pl: 0,
  pr: 0.5,
  minHeight: 58,
  '& .MuiTab-iconWrapper': {
    marginLeft: 'auto',
  },
};

const desktopTabSx = {
  ...baseTabSx,
  minHeight: 54,
  py: 2,
  px: 0.5,
  borderBottom: 1,
  borderColor: 'border.light',
};

const Tabs = ({ pages }: PropsWithChildren<TabsProps>) => {
  const { isMobile, isDesktop } = useResponsive();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname: currentPathname, search } = useLocation();

  const toggleDrawer =
    (open: boolean, callback?: (to?: string) => void) =>
    (
      e: React.KeyboardEvent<HTMLDivElement> | React.MouseEvent<HTMLDivElement>,
    ) => {
      setIsDrawerOpen(open);

      if (callback && typeof callback === 'function') {
        callback();
      }
    };
  const currentTab = pages.find(
    ({ path: to }) => to === currentPathname || `${to}/` === currentPathname,
  );

  const pathname = currentTab?.path;

  return (
    <>
      {isMobile() && currentTab && (
        <>
          <MUITabs
            variant="fullWidth"
            value={pathname}
            onClick={toggleDrawer(true)}
            data-testid={'tabs-mobile'}
          >
            <Tab
              label={currentTab.title}
              icon={<AngleDown />}
              iconPosition="end"
              value={pathname}
              sx={mobileTabSx}
            />
          </MUITabs>

          {/* drawer */}
          <Drawer isDrawerOpen={isDrawerOpen} toggleDrawer={toggleDrawer}>
            <List sx={{ p: 0 }}>
              {pages.map(({ title, path: to, disabled, dataTestId }) => (
                <Fragment key={`mobile-list-${to}`}>
                  <ListItem key={title} disablePadding>
                    <ListItemButton
                      onClick={toggleDrawer(false, () =>
                        navigate(`${to}${search}`),
                      )}
                      // TODO: style should be overridden at theme level
                      selected={to === pathname}
                      disabled={disabled}
                      data-testid={dataTestId}
                    >
                      <ListItemText primary={title} />
                    </ListItemButton>
                  </ListItem>
                  <Divider component="li" />
                </Fragment>
              ))}
            </List>
          </Drawer>
        </>
      )}
      {isDesktop() && (
        <MUITabs
          variant="scrollable"
          scrollButtons="auto"
          value={pathname}
          data-testid={'tabs-desktop'}
        >
          {pages.map(({ title, path: to, disabled, dataTestId }) => (
            <Tab
              component={RouterLink}
              key={`tab-${to}`}
              label={title}
              value={to}
              disabled={disabled}
              to={`${to}${search}`}
              sx={desktopTabSx}
              data-testid={dataTestId}
            />
          ))}
        </MUITabs>
      )}
    </>
  );
};

export default Tabs;
